import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Box, Text } from '@chakra-ui/react';
import i18n, { t } from '#i18n';
import { convertToLocalTimeZone } from '#utils/timeZone';

import { VerticalHoverLinePlugin } from './chartPlugins';
import { getChartOptions } from './chartOptions';
import { ChartLoadingSpinner } from './ChartLoadingSpinner';
import { NoDataFound } from '../NoDataFound';
import { generateDateRange } from '../../utils/generateDateRange';

ChartJS.defaults.color = 'white';
ChartJS.register(
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  VerticalHoverLinePlugin,
);

const formatDate = (dateStr) => {
  const formatter = new Intl.DateTimeFormat(i18n.locale, {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
  const date = new Date(dateStr);

  return formatter.format(date);
};

const extractValues = (stats, dates) => {
  const statMap = new Map(stats.map((stat) => [
    convertToLocalTimeZone(stat.date, 'yyyy-MM-dd'), stat.value,
  ]));
  return dates.map((date) => statMap.get(date) || 0);
};

const isDataEmpty = (data) => !data?.aggregatedStats?.length;

const BASE_DATASET_CONFIG = {
  pointHoverRadius: 5,
  pointHitRadius: 10,
  borderWidth: 2,
  lineTension: 0,
  pointRadius: 0,
};
const CHART_COLORS = [
  '#7C1158', '#4421AF',
  '#1A53FF', '#0D88E6',
  '#00B7C7', '#5AD45A',
  '#8BE04E', '#EBDC78',
];
const AGGREGATED_COLOR = '#E4003A';

const getChartData = (data, dateRange, granularity) => {
  if (isDataEmpty(data)) return null;

  const { reviewerStats, aggregatedStats } = data;
  const dates = generateDateRange(dateRange.startDate, dateRange.endDate, granularity);
  const formattedDates = dates.map(formatDate);

  const datasets = [
    {
      ...BASE_DATASET_CONFIG,
      label: t('stats.aggregated'),
      data: extractValues(aggregatedStats, dates),
      borderColor: AGGREGATED_COLOR,
      backgroundColor: AGGREGATED_COLOR,
    },
    ...reviewerStats.map(({ reviewer, stats }, idx) => ({
      ...BASE_DATASET_CONFIG,
      label: reviewer.name,
      data: extractValues(stats, dates),
      borderColor: CHART_COLORS[idx],
      backgroundColor: CHART_COLORS[idx],
    })),
  ];

  return { labels: formattedDates, datasets };
};

export const PerformanceLineChart = ({
  title,
  data,
  dataType,
  dateRange,
  granularity,
  isLoading,
}) => (
  <>
    <Text fontWeight="semibold" marginBottom={4}>{title}</Text>
    <Box
      display="flex"
      flexGrow={1}
      justifyContent="center"
      alignItems="center"
    >
      {isLoading && <ChartLoadingSpinner />}
      {!isLoading && isDataEmpty(data) && <NoDataFound />}
      {!isLoading && !isDataEmpty(data) && (
        <Line
          options={getChartOptions(dataType)}
          data={getChartData(data, dateRange, granularity)}
        />
      )}
    </Box>
  </>
);
